import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["value", "readOnly", "error", "label", "errorText", "disabled", "inverted", "width", "minHeight", "ariaLabel", "required", "bodyConfig", "color", "typescale", "id", "iconConfig", "type", "Tooltip", "tooltipTitle", "tooltipContent", "helperText", "Body", "Micro", "maxLength", "inputId"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cuid from 'cuid';
import UNSAFE_SetEnvRef from '@uie/set-env-ref';
import Icon from '@vds-core/icons';
import { calculateRem } from '@uie/javascript-style-helpers';
import { BodyConfig, TypographyConfig, Micro, Body } from '@vds-core/typography';
import { colors, calculateSpacing } from '@vds-core/theme';
import { withVDSManager } from '@vds-core/utilities';
import CharacterCounter from './CharacterCounter';
import { Label, HelperText, ErrorText, showError } from '@vds-core/form-elements';
var propTypes = {
  /**
   * If provided, the Input will be rendered in the Disabled State.
   */
  disabled: PropTypes.bool,

  /**
   * Inverts the colors of the TextArea component.
   */
  inverted: PropTypes.bool,

  /**
   * If provided, will provide the defaultValue for the input field.
   */
  defaultValue: PropTypes.any,

  /**
   * If provided, the Input will be rendered in the ReadOnly State.
   */
  readOnly: PropTypes.bool,

  /**
   * The prop for the HTML name Attribute. This is used to specify a name for an input element. It is used to reference the form-data after submitting the form or to reference the element.
   */
  name: PropTypes.string,

  /**
   *  Determines what event this component will check for an error, options are 'onBlur' or 'onChange'.
   */
  errorEvent: PropTypes.oneOf(['blur', 'change']),

  /**
   * Boolean or Function that returns a boolean value that determines if component should show the error state/error message. Function receives the 'event' object  on input change.
   */
  error: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),

  /**
   * If provided, an in the error state, the Input will render error text.
   */
  errorText: PropTypes.string,

  /**
   * If provided, will provide the context for the 'label' on the input field.
   */
  label: PropTypes.string,

  /**
   * If provided, will override the default sizing widths for the small and large datepicker input.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * If provided, adds min-height to Input
   */
  minHeight: PropTypes.oneOf(['2X', '4X', '8X']),

  /**
   * If provided, this is a callback function for when input text is changed.
   */
  onChange: PropTypes.func,

  /**
   * If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * When true, will add the required attribute to the input element, if false, a string of 'Optional' next to the label
   */
  required: PropTypes.bool,

  /**
   * String, array or node containing the title of the TooltipDialog
   */
  tooltipTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),

  /**
   * String, array or node containing the body content of the TooltipDialog
   */
  tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),

  /**
   * If provided, will contain the string for the helper text
   */
  helperText: PropTypes.string,

  /**
   * @ignore - config for passing through icons
   */
  iconConfig: PropTypes.object,

  /**
   * @ignore - config for passing through other colors
   */
  color: PropTypes.object,

  /**
   * @ignore - config for passing though typography
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore - passes through typescale via sting if there's more than one option [example 'VDS' instead of 'MVP']
   */
  typescale: PropTypes.string,

  /**
   * if passed, Input is controlled by the parent component
   */
  value: PropTypes.string,

  /**
   * @ignore If passed, new Body component will render
   */
  Body: PropTypes.func,

  /**
   * @ignore If passed, new Micro component will render
   */
  Micro: PropTypes.func,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the input element.
   */
  inputId: PropTypes.string,

  /**
   * @ignore If passed, the Tooltip will render
   */
  Tooltip: PropTypes.func,

  /**
   *  If passed, sets Max character length for input field
   */
  maxLength: PropTypes.number
};
var defaultProps = {
  disabled: false,
  inverted: false,
  error: undefined,
  errorEvent: 'blur',
  readOnly: false,
  label: undefined,
  required: true,
  color: colors,
  tooltipTitle: undefined,
  tooltipContent: undefined,
  helperText: undefined,
  bodyConfig: BodyConfig,
  iconConfig: undefined,
  typescale: TypographyConfig.getTypescale(),
  Body: Body,
  Micro: Micro,
  Tooltip: undefined,
  width: '100%',
  minHeight: '2X'
};

var _getColor = function _getColor(disabled, inverted, colors) {
  if (!inverted && !disabled) {
    return colors.black;
  } else if (disabled) {
    return inverted ? colors.coolGray10 : colors.coolGray3;
  } else {
    return colors.white;
  }
};

var ComponentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ComponentContainer",
  componentId: "sc-1rev0p0-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: flex;\n  height: 100%;\n  flex-direction: column;\n  width: ", ";\n  max-width: ", ";\n  min-width: ", ";\n"])), function (_ref) {
  var containerWidth = _ref.containerWidth;
  return typeof containerWidth === 'number' ? calculateRem(containerWidth) : containerWidth;
}, calculateRem(596), calculateRem(182));
var StyledContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledContainer",
  componentId: "sc-1rev0p0-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1 1 auto;\n  position: relative;\n  flex-direction: column;\n"])));
var TextareaContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "TextareaContainer",
  componentId: "sc-1rev0p0-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: flex;\n  position: relative;\n  align-items: stretch;\n  min-height: ", ";\n  background-color: ", ";\n  box-sizing: border-box;\n  border: ", " solid\n    ", ";\n  border-bottom: ", " solid\n    ", ";\n  ", ";\n\n  ", ";\n  ", ";\n"])), function (_ref2) {
  var minHeight = _ref2.minHeight;
  return calculateRem(minHeight);
}, function (_ref3) {
  var color = _ref3.color,
      inverted = _ref3.inverted;
  return inverted ? color.black : color.white;
}, calculateRem(1), function (_ref4) {
  var color = _ref4.color,
      error = _ref4.error,
      errorState = _ref4.errorState,
      disabled = _ref4.disabled,
      focused = _ref4.focused,
      inverted = _ref4.inverted;
  return focused && inverted ? color.white : focused ? color.black : (error || errorState) && !disabled ? color.error : inverted ? color.coolGray10 : color.coolGray3;
}, calculateRem(1), function (_ref5) {
  var color = _ref5.color,
      disabled = _ref5.disabled,
      inverted = _ref5.inverted;
  return _getColor(disabled, inverted, color);
}, function (_ref6) {
  var color = _ref6.color,
      readOnly = _ref6.readOnly,
      inverted = _ref6.inverted;
  return readOnly && "\n      border: ".concat(calculateRem(1), " solid ").concat(inverted ? color.coolGray10 : color.coolGray3, ";\n  ");
}, function (_ref7) {
  var color = _ref7.color,
      readOnly = _ref7.readOnly,
      error = _ref7.error,
      errorState = _ref7.errorState,
      disabled = _ref7.disabled,
      inverted = _ref7.inverted;
  return !disabled && !readOnly && "\n      &:hover {\n        border: ".concat(calculateRem(1), " solid ").concat(inverted ? color.white : color.black, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat(error || errorState ? color.error : inverted ? color.white : color.black, ";\n  }\n  ");
}, function (_ref8) {
  var color = _ref8.color,
      error = _ref8.error,
      errorState = _ref8.errorState,
      disabled = _ref8.disabled,
      focused = _ref8.focused,
      readOnly = _ref8.readOnly;
  return (error || errorState) && !disabled && !readOnly && "\n    &::before {\n      background:  ".concat(color.orange, ";\n      box-shadow: none;\n      content: '';\n      height: ").concat(calculateRem(4), ";\n      left: ").concat(-1, "px;\n      position: absolute;\n      bottom: ").concat(-1, "px;\n      width: calc(100% + ").concat(calculateRem(2), ");\n      z-index: 1;\n    }\n  ");
});
var ActionWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionWrapper",
  componentId: "sc-1rev0p0-3"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  height: ", ";\n  width: ", ";\n  padding: ", ";\n"])), calculateRem(20), calculateRem(20), calculateRem(12, 12, 12, 0));
var IconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "IconContainer",
  componentId: "sc-1rev0p0-4"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  align-items: center;\n  outline: none;\n  pointer-events: none;\n"])));
var TextareaBackdrop = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextareaBackdrop",
  componentId: "sc-1rev0p0-5"
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  box-sizing: border-box;\n  color: transparent;\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  letter-spacing: ", ";\n  padding: ", ";\n  white-space: pre-wrap;\n  word-wrap: break-word;\n"])), function (_ref9) {
  var body = _ref9.body;
  return body.fontFamily;
}, function (_ref10) {
  var body = _ref10.body;
  return calculateRem(body.fontSize);
}, function (_ref11) {
  var body = _ref11.body;
  return calculateRem(body.lineHeight);
}, function (_ref12) {
  var body = _ref12.body;
  return calculateRem(body.letterSpacing);
}, calculateSpacing('3X'));
var HighlightMark = /*#__PURE__*/styled.mark.withConfig({
  displayName: "HighlightMark",
  componentId: "sc-1rev0p0-6"
})(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n  color: transparent;\n  background-color: ", ";\n"])), function (_ref13) {
  var color = _ref13.color,
      inverted = _ref13.inverted;
  return inverted ? color.coolGray6 : color.coolGray3;
});
var StyledTextarea = /*#__PURE__*/styled.textarea.withConfig({
  displayName: "StyledTextarea",
  componentId: "sc-1rev0p0-7"
})(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  width: 100%;\n  flex: 1 1 auto;\n  border: none;\n  box-sizing: border-box;\n  background-color: transparent;\n  caret-color: ", ";\n  color: ", ";\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  letter-spacing: ", ";\n  margin: 0;\n  ::-ms-clear,\n  ::-ms-reveal {\n    display: none;\n  }\n  &::-webkit-outer-spin-button,\n  &::-webkit-inner-spin-button {\n    /* display: none; <- Crashes Chrome on hover */\n    -webkit-appearance: none;\n    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */\n  }\n\n  &:focus {\n    outline: none;\n  }\n  padding: 0;\n  resize: none;\n  ::-webkit-scrollbar {\n    display: none;\n  }\n  scrollbar-width: none;\n  -ms-overflow-style: none;\n"])), function (_ref14) {
  var color = _ref14.color,
      inverted = _ref14.inverted;
  return inverted ? color.white : color.black;
}, function (_ref15) {
  var color = _ref15.color,
      inverted = _ref15.inverted,
      disabled = _ref15.disabled;
  return _getColor(disabled, inverted, color);
}, function (_ref16) {
  var body = _ref16.body;
  return body.fontFamily;
}, function (_ref17) {
  var body = _ref17.body;
  return calculateRem(body.fontSize);
}, function (_ref18) {
  var body = _ref18.body;
  return calculateRem(body.lineHeight);
}, function (_ref19) {
  var body = _ref19.body;
  return calculateRem(body.letterSpacing);
});
var TextareaWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextareaWrapper",
  componentId: "sc-1rev0p0-8"
})(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  flex: 1;\n  position: relative;\n  width: 100%;\n  padding: ", ";\n"])), calculateSpacing('3X'));
var FlexedRowContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexedRowContainer",
  componentId: "sc-1rev0p0-9"
})(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1 1 auto;\n  flex-direction: row;\n  position: relative;\n  justify-content: space-between;\n"])));
var FlexedColContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexedColContainer",
  componentId: "sc-1rev0p0-10"
})(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var characterLimitErrorText = 'You have exceeded the character limit';

var TextArea = /*#__PURE__*/function (_React$Component) {
  _inherits(TextArea, _React$Component);

  var _super = _createSuper(TextArea);

  function TextArea(props) {
    var _this2;

    _classCallCheck(this, TextArea);

    _this2 = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this2), "componentDidUpdate", function (prevProps, prevState) {
      if (prevProps.value !== _this2.props.value && _this2.isControlled) {
        _this2.setState({
          value: _this2.props.value
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onChange", function (e) {
      var _this2$props = _this2.props,
          errorEvent = _this2$props.errorEvent,
          error = _this2$props.error,
          required = _this2$props.required;

      var _this = _assertThisInitialized(_this2);

      _this2.props.onChange && _this2.props.onChange(e);
      if (required && e.target.value.length > 0) _this2.setState({
        emptyError: false
      });

      if (_this2.props.maxLength) {
        _this2.setState({
          maxLengthError: e.target.value.length > _this2.props.maxLength
        });
      } // check for error on change


      errorEvent && errorEvent === 'change' && showError(e, _this);

      _this2._setTextareaHeight();

      return !_this2.isControlled && _this2.setState({
        value: e.target.value
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "_calculateMinHeight", function (scaleName) {
      var factor = 44;
      var heightScale = {
        '2X': 2,
        '4X': 4,
        '8X': 8
      };
      var minHeight = heightScale[scaleName] * factor;

      if (!minHeight) {
        console.error('minHeight is not supported by Input height scale!');
        return heightScale['2X'] * factor; //return 88px as default
      }

      return minHeight;
    });

    _defineProperty(_assertThisInitialized(_this2), "_setTextareaHeight", function () {
      var topBottomBorderWidth = 2;
      var topBottomSpacing = 24;

      var minHeight = _this2._calculateMinHeight(_this2.props.minHeight); //textareaMinHeight without top and bottom borders and spacing


      var textareaMinHeight = minHeight - topBottomBorderWidth - topBottomSpacing;
      _this2.textareaElement.style.height = 'auto';

      if (_this2.textareaElement.scrollHeight > textareaMinHeight) {
        _this2.textareaElement.style.height = _this2.textareaElement.scrollHeight + 'px';
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_determineValue", function () {
      var _this2$props2 = _this2.props,
          value = _this2$props2.value,
          defaultValue = _this2$props2.defaultValue;

      if (value || defaultValue) {
        return value;
      } else {
        return _this2.state.value;
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_overflowHighlighter", function () {
      var _this2$props3 = _this2.props,
          maxLength = _this2$props3.maxLength,
          color = _this2$props3.color,
          inverted = _this2$props3.inverted;
      var value = _this2.state.value;
      var valueLength = value ? value.length : 0;
      var normalText = value.substring(0, maxLength);
      var highlightedText = value.substring(maxLength, valueLength);
      return /*#__PURE__*/React.createElement(Fragment, null, normalText, /*#__PURE__*/React.createElement(HighlightMark, {
        inverted: inverted,
        color: color
      }, highlightedText));
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderIcon", function () {
      var _this2$props4 = _this2.props,
          iconConfig = _this2$props4.iconConfig,
          color = _this2$props4.color,
          inverted = _this2$props4.inverted;
      return /*#__PURE__*/React.createElement(IconContainer, {
        tabIndex: -1
      }, /*#__PURE__*/React.createElement(Icon, {
        tabIndex: -1,
        hasStroke: true,
        data: iconConfig,
        size: "medium",
        name: 'error',
        color: color.black,
        inverted: inverted
      }));
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderHelperText", function () {
      var _this2$props5 = _this2.props,
          helperText = _this2$props5.helperText,
          helperTextPlacement = _this2$props5.helperTextPlacement,
          bodyConfig = _this2$props5.bodyConfig,
          disabled = _this2$props5.disabled,
          inverted = _this2$props5.inverted,
          color = _this2$props5.color,
          typescale = _this2$props5.typescale,
          Body = _this2$props5.Body;
      return /*#__PURE__*/React.createElement(HelperText, {
        helperText: helperText,
        helperTextPlacement: "bottom",
        bodyConfig: bodyConfig,
        disabled: disabled,
        inverted: inverted,
        color: color,
        typescale: typescale,
        Body: Body
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "_onFocus", function () {
      if (!_this2.props.readOnly) {
        _this2.setState({
          focused: true
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onBlur", function (e) {
      var _this2$props6 = _this2.props,
          errorEvent = _this2$props6.errorEvent,
          error = _this2$props6.error,
          required = _this2$props6.required;

      var _this = _assertThisInitialized(_this2);

      if (!_this2.props.readOnly) {
        _this2.setState({
          focused: false
        });
      }

      if (required && !e.target.value) _this2.setState({
        emptyError: true
      });
      errorEvent === 'blur' && showError(e, _this);
    });

    _this2.state = {
      value: _this2.props.value ? _this2.props.value : _this2.props.defaultValue ? _this2.props.defaultValue : '',
      hidden: true,
      focused: false,
      error: _this2.props.error === true,
      emptyError: false,
      maxLengthError: false
    };
    return _this2;
  }

  _createClass(TextArea, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.isControlled = this.props.value !== undefined;
      window.addEventListener('resize', this._setTextareaHeight);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      window.removeEventListener('resize', this._setTextareaHeight);
    }
  }, {
    key: "render",
    value: function render() {
      var _React$createElement,
          _this3 = this;

      var _this$props = this.props,
          value = _this$props.value,
          readOnly = _this$props.readOnly,
          errorProp = _this$props.error,
          label = _this$props.label,
          errorTextProp = _this$props.errorText,
          disabled = _this$props.disabled,
          inverted = _this$props.inverted,
          width = _this$props.width,
          minHeight = _this$props.minHeight,
          ariaLabel = _this$props.ariaLabel,
          required = _this$props.required,
          bodyConfig = _this$props.bodyConfig,
          color = _this$props.color,
          typescale = _this$props.typescale,
          id = _this$props.id,
          iconConfig = _this$props.iconConfig,
          type = _this$props.type,
          Tooltip = _this$props.Tooltip,
          tooltipTitle = _this$props.tooltipTitle,
          tooltipContent = _this$props.tooltipContent,
          helperText = _this$props.helperText,
          Body = _this$props.Body,
          Micro = _this$props.Micro,
          maxLength = _this$props.maxLength,
          inputId = _this$props.inputId,
          rest = _objectWithoutProperties(_this$props, _excluded);

      var _this$state = this.state,
          focused = _this$state.focused,
          errorState = _this$state.error,
          emptyError = _this$state.emptyError,
          maxLengthError = _this$state.maxLengthError;
      var refKey = UNSAFE_SetEnvRef(); // Error state has preference over component's error
      // Because errorProp might be a function, we first need to check if prop is a bool and only then assign its value

      var error = maxLengthError || emptyError || errorState || typeof errorProp === 'boolean' && errorProp;
      var fieldId = inputId ? inputId : cuid();
      var body = bodyConfig.getStyles(typescale, 'desktop', 'large');
      var renderIcon = error && !focused && !readOnly && !disabled;
      var labelId = fieldId + ' label';
      var trueLabel = label ? label : '';
      var valueLength = this.state.value.length;
      var accessLabel = ariaLabel ? ariaLabel : !required ? trueLabel + ' Optional Input Field' : trueLabel + ' Input Field';

      if (maxLength) {
        if (valueLength <= maxLength) {
          accessLabel = "".concat(accessLabel, " ").concat(valueLength, " out of ").concat(maxLength, " characters entered");
        } else {
          accessLabel = "".concat(accessLabel, " ").concat(maxLength - valueLength, " characters");
        }
      }

      var errorText = emptyError ? "You must enter a ".concat(label) : maxLengthError ? characterLimitErrorText : errorTextProp;
      error && !errorText && console.error('Error Text for Input MUST be provided');
      return /*#__PURE__*/React.createElement(ComponentContainer, {
        containerWidth: width
      }, /*#__PURE__*/React.createElement(StyledContainer, {
        id: id,
        "data-testid": "test-container",
        helperText: helperText
      }, label && /*#__PURE__*/React.createElement(Label, (_React$createElement = {
        typescale: typescale,
        Body: Body,
        Micro: Micro,
        width: width,
        disabled: disabled,
        inverted: inverted,
        color: color,
        required: required,
        label: label,
        Tooltip: Tooltip,
        tooltipTitle: tooltipTitle,
        tooltipContent: tooltipContent,
        type: "textarea",
        "data-testid": "test-label",
        id: labelId
      }, _defineProperty(_React$createElement, "width", width), _defineProperty(_React$createElement, "aria-label", label), _defineProperty(_React$createElement, "htmlFor", fieldId), _defineProperty(_React$createElement, "focusState", true), _defineProperty(_React$createElement, "labelMarginBottom", calculateSpacing('1X')), _defineProperty(_React$createElement, "overflowEllipsis", true), _defineProperty(_React$createElement, "helperTextPlacement", 'bottom'), _defineProperty(_React$createElement, "helperText", helperText), _React$createElement)), /*#__PURE__*/React.createElement(TextareaContainer, {
        color: color,
        error: error,
        errorState: error,
        disabled: disabled,
        inverted: inverted,
        helperText: helperText,
        errorText: errorText,
        label: label,
        readOnly: readOnly,
        onFocus: this._onFocus,
        onBlur: this._onBlur,
        focused: this.state.focused,
        minHeight: this._calculateMinHeight(minHeight)
      }, /*#__PURE__*/React.createElement(TextareaWrapper, null, maxLength && /*#__PURE__*/React.createElement(TextareaBackdrop, {
        body: body,
        "aria-hidden": "true"
      }, this._overflowHighlighter()), /*#__PURE__*/React.createElement(StyledTextarea, _extends({
        "data-testid": "test-input",
        body: body,
        color: color,
        "aria-label": helperText ? "".concat(accessLabel, " ").concat(helperText) : accessLabel,
        id: fieldId,
        autocomplete: "off"
      }, rest, {
        placeholder: null,
        disabled: disabled,
        inverted: inverted,
        error: error,
        required: false,
        readOnly: readOnly,
        onChange: this._onChange,
        value: this._determineValue(),
        maxLength: maxLength ? Math.round(maxLength * 1.2) : ''
      }, _defineProperty({}, refKey, function (elem) {
        return _this3.textareaElement = elem;
      })))), /*#__PURE__*/React.createElement(ActionWrapper, null, renderIcon && this._renderIcon())), /*#__PURE__*/React.createElement(FlexedRowContainer, null, /*#__PURE__*/React.createElement(FlexedColContainer, null, error && errorText && !disabled && !readOnly && /*#__PURE__*/React.createElement(ErrorText, {
        bodyConfig: bodyConfig,
        typescale: typescale,
        errorText: errorText,
        inverted: inverted,
        error: error,
        errorLabelSpacing: calculateRem(8),
        color: color
      }), helperText && this._renderHelperText()), /*#__PURE__*/React.createElement(FlexedColContainer, null, !readOnly && maxLength && /*#__PURE__*/React.createElement(CharacterCounter, {
        maxLength: maxLength,
        bodyConfig: bodyConfig,
        color: _getColor(readOnly || disabled, inverted, color),
        typescale: typescale,
        Body: Body,
        value: this.state.value
      })))));
    }
  }]);

  return TextArea;
}(React.Component);

TextArea.defaultProps = defaultProps;
TextArea.propTypes = propTypes;
/**
 * @component
 */

export default withVDSManager(TextArea);